import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { HelpCircle, ArrowLeft, ChevronDown } from "lucide-react";
import { faqSchema, jsonLdScript } from "@/lib/seo-schema";

/**
 * Public Frequently-Asked-Questions page.
 *
 * Items are deliberately phrased as if the asker were a club secretary
 * talking to a friendly human — not a developer. New items can be added
 * to the FAQ_ITEMS array below; the rendering picks them up automatically.
 *
 * Every item carries `aText` — a plain-string answer used to emit the
 * FAQPage JSON-LD (rendering JSX inside a schema string is a mess and
 * strips markdown that AI engines might quote). The visible answer stays
 * JSX so we can hyperlink cross-references, keyboard chips, etc.
 */
const FAQ_ITEMS = [
  {
    q: "Can I combine several competitions into a single top-5 leaderboard?",
    aText:
      "Yes — on the Competitions page there's a checkbox column next to every row and an 'Aggregate scores' button in the toolbar. Tick two or more competitions and click Aggregate to see a top-5 leaderboard combining just those rounds. Only each photographer's highest-scoring image in each competition counts, so a photographer with three images (18/15/12) in one round contributes 18 to that column, not 45. The modal shows the top-5 with per-competition scores + image titles, a total, a Print PDF button (A4 portrait), and a winner CSV export. If you use the same selection often — e.g. 'PDI rounds 1-4' — use the 'Presets ▾' button to save it under a name and re-apply it with one click next month.",
    a: (
      <>
        Yes — on the Competitions page there&rsquo;s a checkbox column next to
        every row and an <b>Aggregate scores</b> button in the toolbar. Tick two
        or more competitions and click Aggregate to see a top-5 leaderboard
        combining just those rounds.
        <br /><br />
        Only each photographer&rsquo;s <b>highest-scoring image in each
        competition</b> counts, so a photographer with three images
        (18/15/12) in one round contributes 18 to that column, not 45. The
        modal shows the top-5 with per-competition scores plus each image
        title, a Total, a <b>Print PDF</b> button (A4 portrait) and a
        winner CSV export.
        <br /><br />
        If you use the same selection often — e.g. &ldquo;PDI rounds 1-4&rdquo;
        — use the <b>Presets ▾</b> button to save it under a name and
        re-apply it with one click next month.
      </>
    ),
  },
  {
    q: "What's the 'Publishing checklist' banner on the Live Entry screen?",
    aText:
      "It's a pre-flight banner above the entry grid that appears once at least one row has been scored. It shows three signals — rows unsaved, rows missing a score, and how many of the top-3 placements (1st/2nd/3rd) have been assigned. Amber if any are outstanding, emerald with 'Ready to publish' when unsaved=0, missing-scores=0 and top-3=3. It's designed to catch accidental omissions before you print the results or send them out.",
    a: (
      <>
        A subtle pre-flight banner above the entry grid that appears once at
        least one row has been scored. It shows three signals:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>rows unsaved</li>
          <li>rows missing a score</li>
          <li>how many of the top-3 placements (1st / 2nd / 3rd) have been assigned</li>
        </ul>
        <br />
        Amber if any are outstanding, emerald with &ldquo;Ready to publish&rdquo;
        when everything checks out. It&rsquo;s designed to catch accidental
        omissions before you print the results or send them out.
      </>
    ),
  },
  {
    q: "Is my club eligible for the launch offer?",
    aText:
      "The launch offer — Starter plan with unlimited photographers instead of the usual 20-photographer cap — runs until 31 December 2026. Any brand-new club that registers a workspace before that date is automatically tagged for the offer. If you then subscribe to Starter (£60/year) instead of Unlimited, you keep the unlimited-photographer benefit for the life of your subscription. Existing Starter subscribers who signed up before the offer are unaffected and stay on the 20-photographer cap.",
    a: (
      <>
        The launch offer — <b>Starter plan with unlimited photographers</b>{" "}
        instead of the usual 20-photographer cap — runs until{" "}
        <b>31 December 2026</b>. Any brand-new club that registers a
        workspace before that date is automatically tagged for the offer.
        If you then subscribe to Starter (£60/year) instead of Unlimited,
        you keep the unlimited-photographer benefit for the life of your
        subscription.
        <br /><br />
        Existing Starter subscribers who signed up before the offer are
        unaffected and stay on the 20-photographer cap — the offer is
        strictly for new club sign-ups during the launch window.
      </>
    ),
  },
  {
    q: "What is Camera Club Score?",
    aText:
      "Camera Club Score is a web app for camera clubs to run their internal competition program end-to-end — photographer roster, seasons, scoring, placements, and end-of-season statistics. Each club has its own isolated workspace, so two clubs using the service never see each other's data.",
    a: (
      <>
        A web app for camera clubs to run their internal competition program
        end-to-end — photographer roster, seasons, scoring, placements, and
        end-of-season statistics. Each club has its own isolated workspace,
        so two clubs using the service never see each other&rsquo;s data.
      </>
    ),
  },
  {
    q: "How much does Camera Club Score cost?",
    aText:
      "Two plans, both £/year with a 30-day free trial: Starter is £60/year for up to 20 photographers; Unlimited is £80/year with unlimited photographers. Payment is via Stripe. You can cancel any time.",
    a: <>Two plans, both £/year, with a 30-day free trial:
      <ul className="list-disc list-inside mt-2 space-y-1">
        <li><b>Starter</b> — £60/year, up to 20 photographers</li>
        <li><b>Unlimited</b> — £80/year, unlimited photographers</li>
      </ul>
      Payment is via Stripe. Cancel any time.
    </>,
  },
  {
    q: "How do I add a photographer / season / competition?",
    aText:
      "After signing in as an ADMIN or SECRETARY: Photographers → top-nav 'Photographers' → 'New photographer'. Seasons → 'Seasons' → 'New season' (mark one as Active to make it the default for new competitions). Competitions → 'Competitions' → 'New competition' — pick the season, set the date and round number.",
    a: (
      <>
        After signing in as an ADMIN or SECRETARY:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><b>Photographers</b> → top-nav &ldquo;Photographers&rdquo; → &ldquo;New photographer&rdquo;.</li>
          <li><b>Seasons</b> → &ldquo;Seasons&rdquo; → &ldquo;New season&rdquo;. Mark one as &ldquo;Active&rdquo; to make it the default for new competitions.</li>
          <li><b>Competitions</b> → &ldquo;Competitions&rdquo; → &ldquo;New competition&rdquo; — pick the season, set the date and round number.</li>
        </ul>
      </>
    ),
  },
  {
    q: "How is the season Photographer of the Year decided?",
    aText:
      "Each photographer's season is summarised by their total score and their average score across all counted competition entries — no clever weighting, just the numbers as they are. The trophy goes to whoever has the highest total. How the total is built up is configurable in Settings → Scoring rules: (1) Every entry counts (sum of every scored image), (2) Best image per competition, or (3) Top two per competition. Individual competitions can be marked as 'not counted' if they shouldn't feed into the season total (e.g. friendly rounds).",
    a: (
      <>
        Each photographer&rsquo;s season is summarised by their{" "}
        <b>total score</b> and their <b>average score</b> across all of their
        counted competition entries — no clever weighting, just the numbers
        as they are. The trophy goes to whoever has the highest total.
        <br /><br />
        How the total is built up is configurable in{" "}
        <span className="mono">Settings → Scoring rules</span> — your club
        can choose between three rules:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><b>Every entry counts</b> (default) — sum of every scored image across the season.</li>
          <li><b>Best image per competition</b> — only the photographer&rsquo;s highest-scoring image per round contributes.</li>
          <li><b>Top two per competition</b> — the photographer&rsquo;s top two scores per round are added together.</li>
        </ul>
        <br />
        Individual competitions can be marked as &ldquo;not counted&rdquo;
        if they shouldn&rsquo;t feed into the season total (e.g. friendly
        rounds).
      </>
    ),
  },
  {
    q: "Can I import results from a PDI software PDF (or any club-format results PDF)?",
    aText:
      "Yes. On any competition's Live Entry screen the 'Import entries' button accepts CSV, RTF and results PDFs. We parse the standard UK camera-club results layout most PDI competition tools produce — the 'Entrant Scores' section with one photographer per block, half-scores (7½) supported, and placements (1st / 2nd / 3rd / HC) picked up automatically from the awards section. Photographers are matched by name against your club roster, so keep the Photographers list up to date first. Any rows we can't match are shown in a 'skipped' panel so you can fix them and re-import.",
    a: (
      <>
        Yes. On any competition&rsquo;s Live Entry screen there&rsquo;s an{" "}
        <b>Import entries</b> button that accepts CSV, RTF and results PDFs.
        We parse the standard UK camera-club results layout most PDI
        competition tools produce —{" "}
        <span className="mono">&ldquo;<i>Entrant Scores</i>&rdquo;</span>{" "}
        section, one photographer per block, half-scores (7½) supported,
        placements (1st / 2nd / 3rd / HC) picked up from the awards
        section above.
        <br /><br />
        Photographers are matched by name against your club roster, so
        make sure your <span className="mono">Photographers</span> list is
        up to date first. Any rows we can&rsquo;t match are shown in a
        &ldquo;skipped&rdquo; panel so you can fix them and re-import.
      </>
    ),
  },
  {
    q: "Do you support PDI, Print, Colour, Mono and Themed competitions?",
    aText:
      "Yes — when you create a competition you pick its type (PDI / Print) and category (Open / Colour / Mono / Nature / Themed). Both fields show up on the results and PDFs, and you can mix any combination in a single season. A typical UK club season might have Open PDI rounds, a dedicated Nature Print night, a Monochrome Set competition, and a themed round — all side-by-side in the same season league table.",
    a: (
      <>
        Yes — when you create a competition you pick its <b>type</b> (PDI /
        Print) and <b>category</b> (Open / Colour / Mono / Nature / Themed).
        Both fields show up on the results and PDFs, and you can mix any
        combination in a single season. So a typical UK club season might
        have Open PDI rounds, a dedicated Nature Print night, a Monochrome
        Set competition, and a themed round on a specific subject — all
        living side-by-side in the same season league table.
      </>
    ),
  },
  {
    q: "Is there a mobile app? Can I score on a tablet?",
    aText:
      "There's no separate app to install, but Camera Club Score works well as a bookmarked shortcut on iPad and Android tablets — open the site in Safari or Chrome and add it to the home screen using the browser's share menu. The Live Entry grid is designed to be fast on a tablet on competition night: big touch targets, autocomplete by photographer I.D. or name, and a keyboard-first flow if you plug in a Bluetooth keyboard. Every row saves when you press Enter and the grid jumps to the next row.",
    a: (
      <>
        There&rsquo;s no separate app to install, but Camera Club Score
        works well as a bookmarked shortcut on iPad and Android tablets —
        just open the site in Safari or Chrome and add a home-screen
        shortcut using the browser&rsquo;s share menu.
        <br /><br />
        The Live Entry grid is designed to be fast on a tablet on
        competition night — big touch targets, autocomplete by
        photographer I.D. or name, and a keyboard-first flow if you prefer
        to plug in a Bluetooth keyboard. Every row saves when you press{" "}
        <span className="kbd">Enter</span>, and the grid jumps to the next
        row so you can keep pace with the judge.
      </>
    ),
  },
  {
    q: "What is the public share portal — and who can see it?",
    aText:
      "Every club has a private public share link that gives anyone with the URL a read-only view of the season's competition results — no account or sign-in required. Ideal for emailing members, posting on Facebook, or a QR code on the noticeboard. Only competition results are exposed; the season leaderboard, Photographer of the Year rankings and detailed stats stay private to signed-in admins. You can reset the share link at any time from Settings → Public share link — that instantly revokes access for anyone holding the old URL.",
    a: (
      <>
        Every club has a private <b>public share link</b> that gives
        anyone with the URL a read-only view of your season&rsquo;s
        competition results — no account or sign-in required. Ideal for
        emailing members, posting on Facebook, or putting on the club
        noticeboard as a QR code.
        <br /><br />
        Only <b>competition results</b> are exposed on the portal — the
        season leaderboard, Photographer of the Year rankings and detailed
        stats stay private to signed-in admins and secretaries. You can
        reset the share link at any time from{" "}
        <span className="mono">Settings → Public share link</span> —
        that instantly revokes access for anyone holding the old URL.
      </>
    ),
  },
  {
    q: "Can members log in to see just their own results and scores?",
    aText:
      "Yes — invite them as the MEMBER role from Users → Invite user. Members sign in and see a personal dashboard with just their own entries, scores and season history. They can't see other photographers' entries, the club leaderboard, or any admin settings. If you want people to see the results without an account at all, share the public portal link instead.",
    a: (
      <>
        Yes — invite them as the <b>MEMBER</b> role from{" "}
        <span className="mono">Users → Invite user</span>. Members sign in
        and see a personal dashboard with just their own entries, scores
        and season history. They can&rsquo;t see other photographers&rsquo;
        entries, the club leaderboard, or any admin settings.
        <br /><br />
        If you want people to see the results without an account at all,
        share the public portal link (see the question above) instead.
      </>
    ),
  },
  {
    q: "Is our club's data private from other clubs on the platform?",
    aText:
      "Yes — Camera Club Score is multi-tenant: every club has its own isolated workspace and the database enforces the isolation on every single query. Two clubs using the service never see each other's photographers, competitions, results or team members, even by accident. Only the platform superadmin can see the list of clubs (for support), and even then never sees the data inside them.",
    a: (
      <>
        Yes — Camera Club Score is <b>multi-tenant</b>: every club has its
        own isolated workspace and the database enforces the isolation on
        every single query. Two clubs using the service never see each
        other&rsquo;s photographers, competitions, results or team
        members, even by accident. Only the platform superadmin can see
        the list of clubs (for support), and even then never sees the data
        inside them.
      </>
    ),
  },
  {
    q: "Which browser works best on judging night?",
    aText:
      "Any modern browser is fine — Chrome, Edge, Firefox and Safari (desktop or mobile) are all tested. For judging nights we recommend keeping the laptop or tablet plugged into power and using a wired network connection where possible. Each row saves the moment you press Enter, so a steady connection makes the experience zero-friction.",
    a: (
      <>
        Any modern browser is fine — <b>Chrome</b>, <b>Edge</b>,{" "}
        <b>Firefox</b> and <b>Safari</b> (desktop or mobile) are all
        tested. For judging nights we recommend keeping the laptop or
        tablet plugged into power and using a wired network connection
        where possible — each row saves the moment you press{" "}
        <span className="kbd">Enter</span>, so a steady connection makes
        the experience zero-friction.
      </>
    ),
  },
  {
    q: "Can two clubs share data?",
    aText:
      "No — workspaces are completely isolated. If you administer two clubs you'll need two separate accounts with different email addresses, one per club.",
    a: (
      <>
        No — workspaces are completely isolated. If you administer two clubs
        you&rsquo;ll need two separate accounts with different email
        addresses, one per club.
      </>
    ),
  },
  {
    q: "How do I invite a teammate?",
    aText:
      "ADMIN users go to Users → Invite user, enter your teammate's email and pick their role (Secretary / Viewer / Member). They receive an email with a link to choose their own password. The invitation is valid for 7 days.",
    a: (
      <>
        ADMIN users go to <span className="mono">Users → Invite user</span>,
        enter your teammate&rsquo;s email and pick their role (Secretary /
        Viewer / Member). They&rsquo;ll receive an email with a link to
        choose their own password. The invitation is valid for 7 days.
      </>
    ),
  },
  {
    q: "I forgot my password — what do I do?",
    aText:
      "Use the 'Forgot password?' link on the sign-in page — we email you a reset link. If the email doesn't arrive in a few minutes please check your spam folder, then contact mail@cameraclubscore.com.",
    a: (
      <>
        Use the &ldquo;Forgot password?&rdquo; link on the sign-in page.
        We&rsquo;ll email you a reset link. If the email doesn&rsquo;t arrive
        in a few minutes please check your spam folder, then contact{" "}
        <a className="text-amber-400 hover:text-amber-300" href="mailto:mail@cameraclubscore.com">mail@cameraclubscore.com</a>.
      </>
    ),
  },
  {
    q: "I haven't received a verification email — what now?",
    aText:
      "On every page of the app there's a yellow banner if your email isn't yet verified — click 'Resend email' on the banner. If it still doesn't arrive after a few minutes, check spam. If you still can't find it, contact us and we can flip the verified flag manually.",
    a: (
      <>
        On every page of the app there&rsquo;s a yellow banner if your email
        isn&rsquo;t yet verified. Click the &ldquo;Resend email&rdquo; button
        on the banner. If it still doesn&rsquo;t arrive after a few minutes,
        check your spam folder. If you can&rsquo;t find it at all please
        contact us — we can flip the verified flag for you manually.
      </>
    ),
  },
  {
    q: "Can I download all of my data?",
    aText:
      "Yes — and you should, regularly. ADMIN users go to Settings → Backup and click Download backup. You get a single JSON file containing every photographer, season, competition, entry, and team member. Keep a copy on your laptop or in a cloud folder.",
    a: (
      <>
        Yes — and you should, regularly. ADMIN users go to{" "}
        <span className="mono">Settings → Backup</span> and click{" "}
        <b>Download backup</b>. You&rsquo;ll get a single JSON file
        containing every photographer, season, competition, entry, and team
        member. Keep a copy on your laptop or in a cloud folder.
      </>
    ),
  },
  {
    q: "I accidentally deleted something — can I get it back?",
    aText:
      "Only if you have a recent backup. Go to Settings → Restore from a backup, upload your most recent .ccs-backup.json, and choose Merge mode to re-add just the missing rows. This is precisely why we keep nagging you to take backups.",
    a: (
      <>
        Only if you have a recent backup. Go to{" "}
        <span className="mono">Settings → Restore from a backup</span>,
        upload your most recent <span className="mono">.ccs-backup.json</span>,
        and choose <b>Merge</b> mode to re-add just the missing rows. This
        is precisely why we keep nagging you to take backups!
      </>
    ),
  },
  {
    q: "What happens if I delete my account?",
    aText:
      "If you delete your account and you're the only ADMIN in your club, the entire club workspace and all its data is permanently removed. Download a backup first, and consider promoting another team member to ADMIN if you want the club to continue without you.",
    a: (
      <>
        If you delete your account and you&rsquo;re the only ADMIN in your
        club, the entire club workspace and all its data is permanently
        removed. Make sure to download a backup first, and consider
        promoting another team member to ADMIN if you want the club to
        continue without you.
      </>
    ),
  },
  {
    q: "How do I report a bug or request a feature?",
    aText:
      "Email mail@cameraclubscore.com with a clear description, the steps you took, and a screenshot if possible. We're a small team but read every message.",
    a: (
      <>
        Email <a className="text-amber-400 hover:text-amber-300" href="mailto:mail@cameraclubscore.com">mail@cameraclubscore.com</a>{" "}
        with a clear description, the steps you took, and a screenshot if
        possible. We&rsquo;re a small team but read every message.
      </>
    ),
  },
];


export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0); // first one open by default
  const toggle = (i) => setOpenIdx(openIdx === i ? -1 : i);

  const faqLd = faqSchema(
    FAQ_ITEMS.map(({ q, aText }) => ({ q, a: aText })),
  );

  return (
    <div className="min-h-screen" style={{ background: "#0a0a0a", color: "#e5e5e5" }}>
      <Helmet>
        <title>Frequently Asked Questions — Camera Club Score</title>
        <meta
          name="description"
          content="Answers to the questions UK camera club secretaries ask most: pricing, importing PDI software PDFs, PDI vs print, Photographer of the Year rules, public share portal, data privacy, backups, member logins."
        />
        <link rel="canonical" href="https://cameraclubscore.com/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cameraclubscore.com/faq" />
        <meta property="og:title" content="Frequently Asked Questions — Camera Club Score" />
        <meta
          property="og:description"
          content="Answers to the questions UK camera club secretaries ask most about running photo competitions."
        />
        <meta property="og:image" content="https://cameraclubscore.com/og-image.png" />
        <script type="application/ld+json">
          {jsonLdScript(faqLd)}
        </script>
      </Helmet>
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="faq-page">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm muted-text hover:text-amber-400 mb-8"
          data-testid="faq-back-link"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="ccs-label mb-3">Help</div>
        <h1 className="text-4xl font-semibold tracking-tight mb-2 flex items-center gap-3">
          <HelpCircle className="text-amber-400" size={32} /> Frequently asked questions
        </h1>
        <div className="muted-text text-sm mb-10">
          Don&rsquo;t see your question? Email{" "}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:mail@cameraclubscore.com">mail@cameraclubscore.com</a>.
        </div>

        <div className="space-y-2">
          {FAQ_ITEMS.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                style={{ background: "#0f0f0f", border: "1px solid #1f1f1f" }}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={open}
                  aria-controls={`faq-item-answer-${i}`}
                  className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-neutral-900"
                  data-testid={`faq-item-toggle-${i}`}
                >
                  <ChevronDown
                    size={16}
                    className="mt-0.5 shrink-0 text-amber-400 transition-transform"
                    style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}
                  />
                  <span className="font-medium flex-1">{item.q}</span>
                </button>
                {/*
                  Always render the answer in the DOM (search engines and
                  screen readers index the whole content), but visually
                  collapse via CSS when the accordion is closed. Using
                  `hidden` here also keeps ARIA correct for assistive tech.
                */}
                <div
                  id={`faq-item-answer-${i}`}
                  role="region"
                  aria-labelledby={`faq-item-toggle-${i}`}
                  hidden={!open}
                  className="px-5 pb-5 pl-12 text-sm leading-relaxed muted-text"
                  data-testid={`faq-item-answer-${i}`}
                >
                  {item.a}
                </div>
              </div>
            );
          })}
        </div>

        <div className="muted-text text-xs mt-10 pt-6" style={{ borderTop: "1px solid #1f1f1f" }}>
          Want the full small print?{" "}
          <Link to="/terms" className="text-amber-400 hover:text-amber-300">
            Read the Terms &amp; Conditions →
          </Link>
        </div>
      </div>
    </div>
  );
}
